import { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { Store, LogOut, Menu, ChevronDown, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { MENUS, PANEL_SUBTITLE } from "@/components/panel/menus";
import CommandPalette, { CommandPaletteTrigger } from "@/components/panel/CommandPalette";
import { DensityProvider, DensityToggle, Tooltip, EYEBROW } from "@/components/ui-kit";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { LogoMark, LogoType } from "@/components/brand/BrandLogo";

/**
 * `perm` é a chave da tela no catálogo do backend (App\Support\Permissions).
 * O menu só mostra o que o perfil do usuário libera — mesma chave que a API usa
 * para recusar a chamada, então menu e servidor nunca discordam.
 */


export default function PanelLayout({ role }) {
  const { user, logout, can } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  // Menu montado pelas permissões do perfil; seção que fica sem item nenhum
  // some junto, para não sobrar cabeçalho vazio.
  const groups = useMemo(
    () => (MENUS[role] || [])
      .map((g) => ({ ...g, items: g.items.filter((m) => can(m.perm)) }))
      .filter((g) => g.items.length > 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [role, user?.permissions],
  );

  // Seção que contém a rota ativa (para abrir automaticamente).
  const isItemActive = (m) => m.end ? location.pathname === m.to : (location.pathname === m.to || location.pathname.startsWith(m.to + "/"));
  const activeSection = useMemo(
    () => (groups.find((g) => g.items.some(isItemActive)) || {}).section,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [groups, location.pathname],
  );

  // Accordion: seções abertas por padrão; o usuário abre/fecha e o estado persiste por perfil.
  const storeKey = `osp_menu_open_${role}`;
  const [closed, setClosed] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storeKey) || "{}"); } catch { return {}; }
  });
  const isOpen = (section) => !closed[section];
  const toggleSection = (section) => setClosed((c) => {
    const next = { ...c, [section]: !c[section] };
    try { localStorage.setItem(storeKey, JSON.stringify(next)); } catch { /* ignore */ }
    return next;
  });

  /**
   * Sidebar recolhida: só ícones, com o nome em balão.
   *
   * Guardado por PAPEL, como o accordion: quem opera o PDV o dia inteiro quer a
   * tela larga, e quem administra costuma querer os rótulos à vista. São hábitos
   * diferentes na mesma máquina.
   *
   * Vale só no desktop. No celular a sidebar é uma gaveta que já ocupa a tela
   * toda e some ao navegar — recolher ali não devolveria espaço nenhum.
   */
  const collapseKey = `osp_menu_collapsed_${role}`;
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(collapseKey) === "1"; } catch { return false; }
  });
  const toggleCollapsed = () => setCollapsed((v) => {
    const next = !v;
    try { localStorage.setItem(collapseKey, next ? "1" : "0"); } catch { /* ignore */ }
    return next;
  });

  // Ao navegar, garante que a seção da rota ativa esteja aberta (sem forçar as demais).
  useEffect(() => {
    if (activeSection && closed[activeSection]) {
      setClosed((c) => {
        const next = { ...c, [activeSection]: false };
        try { localStorage.setItem(storeKey, JSON.stringify(next)); } catch { /* ignore */ }
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  // fecha o drawer ao navegar (mobile)
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  /**
   * ⌘K / Ctrl+K abre a paleta de comandos.
   *
   * Só com modificador, de propósito: o PDV já escuta F2/F4 no window e o
   * operador digita código de barras em campo livre — um atalho de tecla solta
   * roubaria o foco no meio da venda.
   */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key?.toLowerCase() === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /**
   * A sidebar, nos dois estados.
   *
   * `compacto` só chega verdadeiro no desktop — a gaveta do celular recebe
   * sempre a versão larga.
   *
   * Recolhida, o accordion é IGNORADO e todos os itens aparecem: sem os rótulos
   * das seções não há como abrir a que está fechada, e um item escondido ficaria
   * inalcançável até a pessoa expandir de novo. O acordeão volta ao expandir,
   * com as seções como estavam.
   */
  const renderSidebar = ({ compacto = false, comToggle = false } = {}) => (
    <>
      <Link to="/" data-testid="panel-logo-link"
        className={cn("flex items-center h-16 border-b border-slate-100 shrink-0",
          compacto ? "justify-center px-2" : "gap-2.5 px-5")}>
        <LogoMark size={compacto ? 30 : 34} />
        {!compacto && <LogoType height={20} />}
      </Link>

      <nav className={cn("flex-1 py-4 overflow-y-auto scrollbar-thin", compacto ? "px-2 space-y-2" : "px-4 space-y-1")}>
        {groups.map((g, gi) => {
          const open = compacto || isOpen(g.section);
          return (
            <div key={g.section}>
              {compacto ? (
                /* Sem rótulo de seção não sobra o que escrever, mas o
                   agrupamento continua sendo informação: um traço separa os
                   blocos, e a primeira seção não ganha traço acima. */
                gi > 0 && <div className="mx-2 mb-2 border-t border-slate-100" aria-hidden />
              ) : (
                /* `h-9` e o token `EYEBROW`: a sidebar tinha ficado de fora da
                    migração e ainda repetia à mão o `text-[10px] ... uppercase`
                    que o token substituiu — 10px, um passo abaixo do resto do
                    sistema, e um botão de 27px de altura para abrir a seção. */
                <button onClick={() => toggleSection(g.section)} data-testid={`panel-section-${g.section}`} aria-expanded={open}
                  className="w-full group/sec flex items-center justify-between px-2 h-9 rounded-lg hover:bg-slate-50 transition-colors">
                  <span className={cn(EYEBROW, "text-slate-400 group-hover/sec:text-slate-600 transition-colors")}>{g.section}</span>
                  <ChevronDown size={13} className={`text-slate-300 group-hover/sec:text-slate-500 transition-transform duration-200 ${open ? "" : "-rotate-90"}`} />
                </button>
              )}
              {/* Grade 1fr/0fr anima a altura suavemente (sem “saltar”). */}
              <div className="grid transition-[grid-template-rows] duration-200 ease-out" style={{ gridTemplateRows: open ? "1fr" : "0fr" }}>
                <div className="overflow-hidden">
                  <div className={cn(compacto ? "space-y-1" : "space-y-0.5 pt-0.5 pb-1")}>
                    {g.items.map((m) => (
                      /* O balão só existe recolhido: expandido o rótulo já está
                         na tela, e um balão repetindo o texto ao lado é ruído.
                         `Tooltip` devolve o filho intacto quando `label` é nulo. */
                      <Tooltip key={m.to} label={compacto ? m.label : null} side="right">
                        <NavLink to={m.to} end={m.end} data-testid={`panel-nav-${m.to.split("/").pop() || "home"}`}
                          tabIndex={open ? 0 : -1}
                          /* Recolhido o texto sai do DOM, e sem isto o link
                             ficaria sem nome acessível — o balão do Radix entra
                             como `aria-describedby`, que descreve mas não nomeia. */
                          aria-label={compacto ? m.label : undefined}
                          title={undefined}
                          className={({ isActive }) => cn(
                            "group flex items-center rounded-xl text-[13.5px] transition-colors",
                            compacto ? "justify-center h-11 w-full" : "gap-3 px-3 py-2.5",
                            isActive ? "bg-brand-50 text-brand-700 font-semibold" : "text-slate-600 font-medium hover:bg-slate-50 hover:text-slate-900",
                          )}>
                          {({ isActive }) => (
                            <>
                              <m.icon size={compacto ? 19 : 17} strokeWidth={isActive ? 2.2 : 1.8} className={isActive ? "text-brand-600" : "text-slate-400 group-hover:text-slate-600 transition-colors"} />
                              {!compacto && m.label}
                            </>
                          )}
                        </NavLink>
                      </Tooltip>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </nav>

      <div className={cn("border-t border-slate-100 shrink-0", compacto ? "p-2 space-y-1" : "p-4 space-y-0.5")}>
        {comToggle && (
          <Tooltip label={collapsed ? "Expandir menu" : null} side="right">
            <button onClick={toggleCollapsed} data-testid="panel-collapse-btn"
              aria-expanded={!collapsed}
              aria-label={collapsed ? "Expandir menu" : "Recolher menu"}
              className={cn(
                "w-full flex items-center rounded-xl text-[13.5px] font-medium text-slate-500 hover:bg-slate-50 hover:text-slate-900 transition-colors",
                compacto ? "justify-center h-11" : "gap-3 px-3 py-2.5",
              )}>
              {collapsed ? <PanelLeftOpen size={17} strokeWidth={1.8} className="text-slate-400" />
                         : <PanelLeftClose size={17} strokeWidth={1.8} className="text-slate-400" />}
              {!compacto && "Recolher menu"}
            </button>
          </Tooltip>
        )}

        <Tooltip label={compacto ? "Ver loja" : null} side="right">
          <Link to="/" data-testid="panel-store-link" aria-label={compacto ? "Ver loja" : undefined}
            className={cn("flex items-center rounded-xl text-[13.5px] font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors",
              compacto ? "justify-center h-11" : "gap-3 px-3 py-2.5")}>
            <Store size={17} strokeWidth={1.8} className="text-slate-400" />{!compacto && "Ver loja"}
          </Link>
        </Tooltip>

        <Tooltip label={compacto ? "Sair" : null} side="right">
          <button onClick={() => { logout(); navigate("/login"); }} data-testid="panel-logout-btn"
            aria-label={compacto ? "Sair" : undefined}
            className={cn("w-full flex items-center rounded-xl text-[13.5px] font-medium text-slate-600 hover:bg-red-50 hover:text-red-600 transition-colors",
              compacto ? "justify-center h-11" : "gap-3 px-3 py-2.5")}>
            <LogOut size={17} strokeWidth={1.8} className="text-slate-400" />{!compacto && "Sair"}
          </button>
        </Tooltip>
      </div>
    </>
  );

  return (
    <DensityProvider>
    <div className="h-screen flex bg-slate-100/70 overflow-hidden">
      {/* sidebar fixa (desktop) — largura normal, não redimensiona o conteúdo */}
      {/* A largura anima para o conteúdo não saltar. `shrink-0` continua: quem
          decide a largura é este estado, não a pressão do conteúdo ao lado. */}
      <aside
        data-testid="panel-sidebar"
        data-collapsed={collapsed ? "1" : "0"}
        className={cn(
          "shrink-0 hidden lg:flex flex-col bg-white border-r border-slate-200 transition-[width] duration-200 ease-out",
          collapsed ? "w-[72px]" : "w-64",
        )}
      >
        {renderSidebar({ compacto: collapsed, comToggle: true })}
      </aside>

      {/* drawer (mobile) */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          {/* Botão de verdade: como <div onClick> o fechar era só para mouse. */}
          <button type="button" aria-label="Fechar menu" onClick={() => setMobileOpen(false)}
            className="absolute inset-0 w-full bg-slate-900/40" data-testid="panel-drawer-overlay" />
          <aside className="absolute left-0 top-0 h-full w-72 max-w-[85vw] flex flex-col bg-white border-r border-slate-200 fade-up" data-testid="panel-drawer">
            {renderSidebar()}
          </aside>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center gap-3 px-4 sm:px-6 shrink-0">
          <button onClick={() => setMobileOpen(true)} data-testid="panel-menu-btn" aria-label="Abrir menu"
            className="lg:hidden p-2 -ml-1 text-slate-600 hover:bg-slate-100 rounded-lg"><Menu size={20} /></button>
          <div className="leading-tight min-w-0">
            <p className="text-sm font-semibold text-slate-900 truncate" data-testid="panel-user-name">Bem-vindo de volta, {user?.name?.split(" ")[0]}</p>
            <p className="text-xs text-slate-400">{PANEL_SUBTITLE[role] || "Painel"}</p>
          </div>
          <div className="ml-auto shrink-0 flex items-center gap-2">
            <DensityToggle />
            <CommandPaletteTrigger onClick={() => setPaletteOpen(true)} />
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 overflow-y-auto"><Outlet /></main>
      </div>

      <CommandPalette groups={groups} open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
    </DensityProvider>
  );
}
